import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    Chip,
    Collapse,
    Typography,
    useColorScheme
} from "@mui/material";
import {
    AccessTimeRounded,
    ChildCare,
    Face2Outlined,
    Face3Outlined,
    Face6Outlined,
    SentimentSatisfiedAltOutlined,
    SupervisedUserCircleOutlined
} from "@mui/icons-material";
import { fixTextToSlug, replacePersianNumbers, toPriceWithComma } from "../../../helper/utils";
import { useNavigate } from "react-router-dom";
import GympinPinIcon from "../../../components/GympinPinIcon";

const _ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_GYM = ({ item, isDark: isDarkProp }) => {
    const navigate = useNavigate();
    const { mode, systemMode } = useColorScheme();
    const isDark = isDarkProp !== undefined ? isDarkProp : (mode === "system" ? systemMode : mode) === "dark";
    const [showActiveTime, setShowActiveTime] = useState(false);

    const placeImage = useMemo(() => {
        const defaultMedia = item?.Multimedias?.find(m => m.default) || item?.Multimedias?.[0];
        if (defaultMedia?.Url) {
            const separator = defaultMedia.Url.includes('?') ? '&' : '?';
            return `${defaultMedia.Url}${separator}width=350`;
        }
        return "https://api.gympin.ir/resource/image?Id=11&width=350";
    }, [item?.Multimedias]);

    const discountAmount = useMemo(() => {
        if (item?.MinPriceBeforeDiscount && item?.MinPrice && item.MinPriceBeforeDiscount > item.MinPrice) {
            return item.MinPriceBeforeDiscount - item.MinPrice;
        }
        return null;
    }, [item?.MinPriceBeforeDiscount, item?.MinPrice]);

    const gymUrl = "/place/" + item?.Id + "-" + fixTextToSlug(item?.Name);

    const renderGenderBadge = (gender) => {
        switch (gender) {
            case "MALE":
                return (
                    <Chip
                        key="male"
                        size="small"
                        icon={<SentimentSatisfiedAltOutlined sx={{ fontSize: "0.85rem !important", color: "#0284c7 !important" }} />}
                        label="آقایان"
                        sx={{
                            bgcolor: "rgba(2, 132, 199, 0.08)",
                            color: "#0369a1",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px",
                            border: "1px solid rgba(2, 132, 199, 0.15)"
                        }}
                    />
                );
            case "FEMALE":
                return (
                    <Chip
                        key="female"
                        size="small"
                        icon={<Face3Outlined sx={{ fontSize: "0.85rem !important", color: "#e11d48 !important" }} />}
                        label="خانم‌ها"
                        sx={{
                            bgcolor: "rgba(225, 29, 72, 0.08)",
                            color: "#be123c",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px",
                            border: "1px solid rgba(225, 29, 72, 0.15)"
                        }}
                    />
                );
            case "BOYS":
                return (
                    <Chip
                        key="boys"
                        size="small"
                        icon={<Face6Outlined sx={{ fontSize: "0.85rem !important", color: "#0284c7 !important" }} />}
                        label="پسرها"
                        sx={{
                            bgcolor: "rgba(2, 132, 199, 0.08)",
                            color: "#0369a1",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            case "GIRLS":
                return (
                    <Chip
                        key="girls"
                        size="small"
                        icon={<Face2Outlined sx={{ fontSize: "0.85rem !important", color: "#e11d48 !important" }} />}
                        label="دخترها"
                        sx={{
                            bgcolor: "rgba(225, 29, 72, 0.08)",
                            color: "#be123c",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            case "KIDS":
                return (
                    <Chip
                        key="kids"
                        size="small"
                        icon={<ChildCare sx={{ fontSize: "0.85rem !important", color: "#ca8a04 !important" }} />}
                        label="کودکان"
                        sx={{
                            bgcolor: "rgba(202, 138, 4, 0.08)",
                            color: "#a16207",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            case "NONE":
                return (
                    <Chip
                        key="none"
                        size="small"
                        icon={<SupervisedUserCircleOutlined sx={{ fontSize: "0.85rem !important", color: isDark ? "#a3a3a3 !important" : "#525252 !important" }} />}
                        label="همه"
                        sx={{
                            bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.05)",
                            color: isDark ? "#d4d4d4" : "#525252",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Card
            onClick={() => navigate(gymUrl)}
            dir="rtl"
            sx={{
                mx: 1,
                my: 1.5,
                borderRadius: "20px",
                overflow: "hidden",
                isolation: "isolate",
                transform: "translateZ(0)",
                WebkitMaskImage: "-webkit-radial-gradient(white, black)",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.04)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                outline: "none",
                "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 16px 36px -6px rgba(0, 0, 0, 0.12)",
                    borderColor: "rgba(231, 51, 62, 0.25)"
                },
                "&:hover .gym-card-img": {
                    transform: "scale(1.05)"
                },
                "&:active": {
                    transform: "scale(0.985)"
                }
            }}
        >
            {/* محفظه تصویر با برچسب‌های شناور مدرن */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: 180,
                    borderTopLeftRadius: "19px",
                    borderTopRightRadius: "19px",
                    overflow: "hidden",
                    isolation: "isolate",
                    transform: "translateZ(0)",
                    WebkitMaskImage: "-webkit-radial-gradient(white, black)",
                    bgcolor: "action.hover"
                }}
            >
                <Box
                    component="img"
                    className="gym-card-img"
                    src={placeImage}
                    alt={item?.Name || "باشگاه ورزشی"}
                    loading="lazy"
                    decoding="async"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        borderTopLeftRadius: "19px",
                        borderTopRightRadius: "19px",
                        transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                />

                {/* تگ شناور منطقه در گوشه چپ پایین */}
                {item?.Location?.Name && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            insetInlineEnd: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            bgcolor: isDark ? "rgba(35, 35, 35, 0.85)" : "rgba(30, 30, 30, 0.75)",
                            backdropFilter: "blur(8px)",
                            px: 1.1,
                            py: 0.35,
                            borderRadius: "20px",
                            border: "1px solid rgba(255, 255, 255, 0.18)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                            zIndex: 1
                        }}
                    >
                        <GympinPinIcon size={14} primaryColor="#E3343F" secondaryColor="#ffffff" />
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                color: "#ffffff",
                                lineHeight: 1.2
                            }}
                        >
                            {item.Location.Name}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* بدنه محتوای کارت */}
            <Box sx={{ p: 2, pb: 1.5, flex: 1, display: "flex", flexDirection: "column" }}>
                {/* عنوان باشگاه در سمت راست */}
                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        fontWeight: 800,
                        fontSize: "1rem",
                        color: "text.primary",
                        mb: 1,
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {item?.Name}
                </Typography>

                {/* خط اول: ساعت کاری در راست و جنسیت در چپ */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 28,
                        mb: 0.8,
                        width: "100%"
                    }}
                >
                    {/* دکمه ساعات کاری در سمت راست */}
                    {item?.ActiveTimes ? (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowActiveTime(!showActiveTime);
                            }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.4,
                                bgcolor: showActiveTime ? "rgba(231, 51, 62, 0.12)" : "action.hover",
                                color: showActiveTime ? "primary.main" : "text.secondary",
                                px: 1,
                                py: 0.35,
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                flexShrink: 0,
                                "&:hover": {
                                    bgcolor: "rgba(231, 51, 62, 0.12)",
                                    color: "primary.main"
                                }
                            }}
                        >
                            <AccessTimeRounded sx={{ fontSize: "0.85rem" }} />
                            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700 }}>
                                ساعت کار
                            </Typography>
                        </Box>
                    ) : <Box />}

                    {/* بج جنسیت در سمت چپ */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                        {item?.Genders?.length > 0
                            ? item.Genders.map(gender => renderGenderBadge(gender))
                            : renderGenderBadge("NONE")}
                    </Box>
                </Box>

                {/* خط دوم: ورزش‌ها راست‌چین و تعداد مابقی بلافاصله در انتهای متن */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.6,
                        minHeight: 22,
                        mb: 1.2,
                        width: "100%",
                        overflow: "hidden",
                        flexWrap: "nowrap"
                    }}
                >
                    {item?.Sports && item.Sports.length > 0 ? (
                        <>
                            {item.Sports.slice(0, 3).map((sport, idx) => (
                                <React.Fragment key={`sport-${idx}`}>
                                    {idx > 0 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 3.5,
                                                height: 3.5,
                                                borderRadius: "50%",
                                                bgcolor: "#e7333e",
                                                flexShrink: 0
                                            }}
                                        />
                                    )}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            maxWidth: 95,
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {sport.Name}
                                    </Typography>
                                </React.Fragment>
                            ))}
                            {item.Sports.length > 3 && (
                                <Box
                                    component="span"
                                    dir="rtl"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        color: "text.disabled",
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0
                                    }}
                                >
                                    <Box component="span">+</Box>
                                    <Box component="span">{replacePersianNumbers(String(item.Sports.length - 3))}</Box>
                                </Box>
                            )}
                        </>
                    ) : null}
                </Box>

                {/* باکس تاشوی ساعات کاری */}
                {item?.ActiveTimes && (
                    <Collapse in={showActiveTime} timeout="auto" unmountOnExit>
                        <Alert
                            icon={false}
                            severity="info"
                            sx={{
                                mb: 1.5,
                                py: 0.5,
                                px: 1.25,
                                textAlign: "left",
                                fontSize: "0.72rem",
                                borderRadius: "10px",
                                bgcolor: "rgba(2, 132, 199, 0.06)",
                                color: "#0369a1",
                                border: "1px solid rgba(2, 132, 199, 0.15)",
                                "& .MuiAlert-icon": { color: "#0284c7", p: 0 }
                            }}
                        >
                            {item.ActiveTimes}
                        </Alert>
                    </Collapse>
                )}
            </Box>

            {/* فوتر: بخش قیمت و تخفیف چسبیده به پایین و لبه‌های کارت بدون حاشیه سفید */}
            {discountAmount != null && discountAmount > 0 ? (
                <Box
                    sx={{
                        width: "100%",
                        bgcolor: "#e7333e",
                        color: "#ffffff",
                        py: 0.9,
                        px: 1.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: "auto"
                    }}
                >
                    {/* ستون قیمت در سمت راست: شروع از در راست، و قیمت تخفیف‌نخورده دقیقاً بالای قیمت تخفیف‌خورده */}
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.6 }}>
                        <Typography
                            sx={{
                                color: "rgba(255, 255, 255, 0.9)",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                mb: 0.15
                            }}
                        >
                            شروع از
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.15 }}>
                            {item?.MinPriceBeforeDiscount && item.MinPriceBeforeDiscount > item.MinPrice && (
                                <Typography
                                    sx={{
                                        color: "rgba(255, 255, 255, 0.75)",
                                        textDecoration: "line-through",
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        lineHeight: 1
                                    }}
                                >
                                    {toPriceWithComma(item.MinPriceBeforeDiscount)}
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
                                <Typography
                                    sx={{
                                        color: "#ffffff",
                                        fontSize: "1.02rem",
                                        fontWeight: 900,
                                        lineHeight: 1
                                    }}
                                >
                                    {toPriceWithComma(item.MinPrice)}
                                </Typography>
                                <Box
                                    component="img"
                                    src="/assets/images/svg/toman.svg"
                                    alt="تومان"
                                    sx={{
                                        width: 13,
                                        height: 13,
                                        display: "block",
                                        filter: "brightness(0) invert(1)"
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* خط عمودی جداکننده تخفیف از قیمت */}
                    <Box
                        sx={{
                            width: "1px",
                            height: 32,
                            borderLeft: "1px dashed rgba(255, 255, 255, 0.4)",
                            my: "auto",
                            mx: 1
                        }}
                    />

                    {/* بخش تخفیف با آیکون در سمت چپ */}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <Typography sx={{ color: "#ffffff", fontSize: "0.85rem", fontWeight: 900, lineHeight: 1.1 }}>
                                {toPriceWithComma(discountAmount)}
                            </Typography>
                            <Box
                                component="img"
                                src="/assets/images/svg/toman.svg"
                                alt="تومان"
                                sx={{
                                    width: 12,
                                    height: 12,
                                    display: "block",
                                    filter: "brightness(0) invert(1)"
                                }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, mt: 0.2 }}>
                            <Box
                                component="img"
                                src="/assets/images/discountIcon.svg"
                                alt="تخفیف"
                                sx={{
                                    width: 13,
                                    height: 13,
                                    display: "block",
                                    filter: "brightness(0) invert(1)"
                                }}
                            />
                            <Typography sx={{ color: "#ffffff", fontSize: "0.68rem", fontWeight: 900, lineHeight: 1 }}>
                                تخفیف
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        py: 1,
                        px: 2,
                        mt: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.6,
                        minHeight: 46
                    }}
                >
                    {item?.MinPrice ? (
                        <>
                            <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", fontSize: "0.72rem", fontWeight: 700 }}
                            >
                                شروع از
                            </Typography>
                            <Typography
                                sx={{
                                    color: "text.primary",
                                    fontSize: "1.08rem",
                                    fontWeight: 900,
                                    lineHeight: 1
                                }}
                            >
                                {toPriceWithComma(item.MinPrice)}
                            </Typography>
                            <Box
                                component="img"
                                src="/assets/images/svg/toman.svg"
                                alt="تومان"
                                sx={{
                                    width: 14,
                                    height: 14,
                                    display: "block",
                                    filter: isDark ? "brightness(1.25)" : "none"
                                }}
                            />
                        </>
                    ) : (
                        <Typography sx={{ color: "text.disabled", fontSize: "0.82rem", fontWeight: 600 }}>
                            بدون پلن فعال
                        </Typography>
                    )}
                </Box>
            )}
        </Card>
    );
};

export default React.memo(_ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_GYM);

